import React, {useMemo, useState} from 'react'

import {IndeterminateCheckbox} from './CollapsiblePermissionGroups'
import {resolveCatalogEntry, sortEntriesInModule, type PermissionCatalogEntry} from '../core/permissionGrouping'
import {assignProductionPermissionPlacement} from '../core/productionPermissionGrouping'
import {
  buildPlainEnglishRoleSummary,
  buildSimpleMatrixBuckets,
  moduleAccessLevel,
  SIMPLE_MATRIX_ACTION_LABEL,
  SIMPLE_MATRIX_ACTION_ORDER,
  type SimpleMatrixModuleBucket,
} from '../core/roleMatrixSimpleModel'

const norm = (c: string) => c.trim().toUpperCase()

const largeCheckClass = 'form-check-input m-0 flex-shrink-0'

function actionToggleState(codes: string[], selectedCodes: Set<string>) {
  if (codes.length === 0) return {empty: true as const}
  let on = 0
  for (const c of codes) {
    if (selectedCodes.has(norm(c))) on++
  }
  const all = on === codes.length
  const none = on === 0
  return {empty: false as const, all, none, on, checked: all, indeterminate: !all && !none}
}

function ModulePermissionCard({
  bucket,
  placementCatalog,
  selectedCodes,
  disabled,
  onSetMany,
  onToggle,
}: {
  bucket: SimpleMatrixModuleBucket
  placementCatalog: PermissionCatalogEntry[]
  selectedCodes: Set<string>
  disabled: boolean
  onSetMany: (codes: string[], selected: boolean) => void
  onToggle: (code: string) => void
}) {
  const [detailsOpen, setDetailsOpen] = useState(false)
  const {badge, selected, total} = moduleAccessLevel(bucket.allCodes, selectedCodes)
  const modState = actionToggleState(bucket.allCodes, selectedCodes)

  const byMenu = useMemo(() => {
    const items = sortEntriesInModule(bucket.items)
    const m = new Map<string, PermissionCatalogEntry[]>()
    for (const e of items) {
      const pl = assignProductionPermissionPlacement(e, placementCatalog)
      const key =
        pl.subMenuLabel && pl.subMenuLabel !== '—' ? `${pl.menuLabel} · ${pl.subMenuLabel}` : pl.menuLabel
      if (!m.has(key)) m.set(key, [])
      m.get(key)!.push(e)
    }
    const keys = Array.from(m.keys()).sort((a, b) => a.localeCompare(b))
    return keys.map((k) => ({menuKey: k, rows: m.get(k)!}))
  }, [bucket.items, placementCatalog])

  if (modState.empty) return null

  const badgeClass =
    badge === 'full'
      ? 'badge-light-success text-gray-900'
      : badge === 'partial'
        ? 'badge-light-warning text-gray-900'
        : 'badge-light text-gray-800'

  const badgeLabel =
    badge === 'full' ? 'Full access' : badge === 'partial' ? 'Partial access' : 'No access'

  return (
    <div className='border rounded-3 bg-white shadow-sm mb-4 overflow-hidden'>
      <div className='d-flex flex-wrap align-items-start justify-content-between gap-3 p-4 border-bottom border-gray-200'>
        <div>
          <div className='d-flex flex-wrap align-items-center gap-2 mb-1'>
            <h3 className='fs-5 fw-bold text-gray-900 mb-0'>{bucket.moduleKey}</h3>
            <span className={`badge ${badgeClass}`}>{badgeLabel}</span>
          </div>
          <div className='small text-gray-700'>
            <span className='fw-semibold text-gray-900'>{selected}</span> of {total} permissions selected
          </div>
        </div>
      </div>

      <div className='p-4'>
        <div className='d-flex align-items-center gap-2 mb-4'>
          <IndeterminateCheckbox
            id={`mod-en-${bucket.moduleKey.replace(/\s+/g, '-').toLowerCase()}`}
            checked={modState.checked}
            indeterminate={modState.indeterminate}
            disabled={disabled}
            className={largeCheckClass}
            style={{width: '1.25rem', height: '1.25rem'}}
            title={`Enable or disable all permissions in ${bucket.moduleKey}`}
            onChange={() => {
              if (modState.all) onSetMany(bucket.allCodes, false)
              else onSetMany(bucket.allCodes, true)
            }}
          />
          <label
            className='fw-semibold text-gray-900 mb-0'
            htmlFor={`mod-en-${bucket.moduleKey.replace(/\s+/g, '-').toLowerCase()}`}
            style={{cursor: disabled ? 'default' : 'pointer'}}
          >
            Enable {bucket.moduleKey}
          </label>
        </div>

        <div className='text-uppercase small text-muted fw-bold mb-2' style={{letterSpacing: '0.04em'}}>
          Access
        </div>
        <div className='row g-3'>
          {SIMPLE_MATRIX_ACTION_ORDER.map((ak) => {
            const codes = bucket.byAction[ak]
            if (codes.length === 0) return null
            const st = actionToggleState(codes, selectedCodes)
            if (st.empty) return null
            const id = `act-${bucket.moduleKey}-${ak}`.replace(/\s+/g, '-').toLowerCase()
            return (
              <div className='col-12 col-sm-6 col-xl-4' key={ak}>
                <div className='d-flex align-items-center gap-2 border rounded-2 px-3 py-2 h-100 bg-light'>
                  <IndeterminateCheckbox
                    id={id}
                    checked={st.checked}
                    indeterminate={st.indeterminate}
                    disabled={disabled}
                    className={largeCheckClass}
                    style={{width: '1.15rem', height: '1.15rem'}}
                    title={SIMPLE_MATRIX_ACTION_LABEL[ak]}
                    onChange={() => {
                      if (st.all) onSetMany(codes, false)
                      else onSetMany(codes, true)
                    }}
                  />
                  <label
                    className='mb-0 text-gray-900 small'
                    htmlFor={id}
                    style={{cursor: disabled ? 'default' : 'pointer'}}
                  >
                    {SIMPLE_MATRIX_ACTION_LABEL[ak]}
                  </label>
                </div>
              </div>
            )
          })}
        </div>

        <button
          type='button'
          className='btn btn-link btn-sm text-decoration-none px-0 mt-3 fw-semibold'
          onClick={() => setDetailsOpen((v) => !v)}
        >
          {detailsOpen ? 'Hide details' : 'Show details'} (menu-level)
        </button>

        {detailsOpen ? (
          <div className='mt-3 border rounded-2 bg-white'>
            {byMenu.map(({menuKey, rows}) => (
              <div key={`${bucket.moduleKey}-${menuKey}`}>
                <div className='px-3 py-2 fw-semibold small text-gray-800 bg-light border-bottom'>{menuKey}</div>
                {rows.map((entry) => {
                  const r = resolveCatalogEntry(entry)
                  const code = norm(r.permissionCode)
                  const ck = selectedCodes.has(code)
                  const hid = `det-${code.replace(/[^A-Z0-9_-]/gi, '_')}`
                  return (
                    <div key={code} className='d-flex align-items-start gap-2 px-3 py-2 border-bottom'>
                      <input
                        id={hid}
                        type='checkbox'
                        className={largeCheckClass}
                        style={{width: '1.1rem', height: '1.1rem', marginTop: '0.15rem'}}
                        checked={ck}
                        disabled={disabled}
                        onChange={() => onToggle(r.permissionCode)}
                      />
                      <label className='mb-0 flex-grow-1' htmlFor={hid} style={{cursor: disabled ? 'default' : 'pointer'}}>
                        <div className='fw-medium text-gray-900'>{r.displayName}</div>
                      </label>
                    </div>
                  )
                })}
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  )
}

export type SimpleRolePermissionPanelProps = {
  catalog: PermissionCatalogEntry[]
  searchQuery: string
  selectedCodes: Set<string>
  disabled: boolean
  onToggle: (permissionCode: string) => void
  onSetMany: (codes: string[], selected: boolean) => void
}

export function SimpleRolePermissionPanel({
  catalog,
  searchQuery,
  selectedCodes,
  disabled,
  onToggle,
  onSetMany,
}: SimpleRolePermissionPanelProps) {
  const modules = useMemo(() => buildSimpleMatrixBuckets(catalog, searchQuery), [catalog, searchQuery])
  const summaryLines = useMemo(() => buildPlainEnglishRoleSummary(modules, selectedCodes), [modules, selectedCodes])

  if (modules.length === 0) {
    return (
      <div className='alert alert-light border text-gray-800'>
        No permissions match your search. Clear the filter or try different keywords.
      </div>
    )
  }

  return (
    <div className='simple-role-permission-panel'>
      <div className='mb-4 p-4 rounded-3 border' style={{borderColor: '#dee2e6', backgroundColor: '#f8f9fa'}}>
        <div className='fw-bold text-gray-900 small text-uppercase mb-2' style={{letterSpacing: '0.05em'}}>
          What this role can access
        </div>
        {summaryLines.length === 0 ? (
          <p className='small text-warning mb-0 fw-semibold'>No permissions selected yet.</p>
        ) : (
          <>
            <p className='small text-gray-800 mb-2'>This role can access:</p>
            <ul className='small text-gray-800 mb-0 ps-3' style={{lineHeight: 1.65}}>
              {summaryLines.map((line, i) => (
                <li key={i}>{line}</li>
              ))}
            </ul>
          </>
        )}
      </div>

      {modules.map((bucket) => (
        <ModulePermissionCard
          key={bucket.moduleKey}
          bucket={bucket}
          placementCatalog={catalog}
          selectedCodes={selectedCodes}
          disabled={disabled}
          onSetMany={onSetMany}
          onToggle={onToggle}
        />
      ))}
    </div>
  )
}
